import { useQuery } from '@tanstack/react-query';
import {
  DependenciesGraphNodeAny,
  DependenciesGraphEdge,
  TaskNode,
  TaskNodeBase,
  useGraphneAPI,
} from '@nexelem/graphne-api';

export type ExtendedTaskNode = TaskNodeBase & {
  nodeType: 'TaskNode';
  children?: ExtendedTaskNode[];
};

export const useGenerateTaskDependencyTree = (taskId: string) => {
  const api = useGraphneAPI();

  return useQuery({
    queryKey: ['task-dependencies-tree', taskId],
    queryFn: async (): Promise<ExtendedTaskNode[]> => {
      const rawData = await api.getGraphneGraphTaskid({ taskId });

      if (!rawData?.entryNode || !rawData.nodes || !rawData.edges) {
        return [];
      }

      const treeData = buildTree(rawData.entryNode, rawData.nodes, rawData.edges);

      if (!treeData?.children) {
        return [];
      }

      return treeData.children;
    },
    enabled: !!taskId,
  });
};

const buildTree = (
  entryNode: TaskNode,
  allNodes: DependenciesGraphNodeAny[],
  edges: DependenciesGraphEdge[],
): ExtendedTaskNode | undefined => {
  const nodeMap = new Map<number, ExtendedTaskNode>();

  const taskNodes = allNodes.filter(
    (node): node is TaskNodeBase & { nodeType: 'TaskNode' } => node.nodeType === 'TaskNode',
  );

  taskNodes.forEach((node) => {
    nodeMap.set(node.nodeId, { ...node, children: [] });
  });

  if (!nodeMap.has(entryNode.nodeId)) {
    nodeMap.set(entryNode.nodeId, { ...entryNode, nodeType: 'TaskNode', children: [] } as ExtendedTaskNode);
  }

  edges.forEach((edge) => {
    const parentNode = nodeMap.get(edge.toNode);
    const childNode = nodeMap.get(edge.fromNode);

    if (parentNode && childNode) {
      if (!parentNode.children?.some((child: ExtendedTaskNode) => child.nodeId === childNode.nodeId)) {
        parentNode.children?.push(childNode);
      }
    }
  });

  return nodeMap.get(entryNode.nodeId);
};
